import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import TasksList from '../../components/crm/TasksList';
import FilterChip from '../../components/crm/FilterChip';
import PaginatedTable from '../../components/admin/PaginatedTable';
import TaskFormDialog from '../../components/admin/TaskFormDialog';
import usePaginatedQuery from '../../hooks/usePaginatedQuery';

export default function Tasks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = searchParams.get('scope') || 'mine';
  const includeDone = searchParams.get('include_done') === 'true';
  // Sprint I M6: Due-today / Overdue / All-time view filters per W1 §3.9.
  const view = searchParams.get('view') || 'all';
  const [showNew, setShowNew] = useState(false);

  const updateParam = (k, v) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v == null || v === '' || v === false) next.delete(k);
      else next.set(k, String(v));
      return next;
    });
  };

  const path = scope === 'mine' ? '/api/admin/tasks/mine' : '/api/admin/tasks';
  const paginator = usePaginatedQuery({
    path,
    responseKey: 'tasks',
    extraParams: {
      include_done: includeDone ? 'true' : '',
      view: view !== 'all' ? view : '',
    },
  });

  const [, setTasks] = useState([]); // eslint-disable-line no-empty-pattern
  const onChange = (next) => {
    setTasks(next);
    paginator.refetch();
  };

  const anyFilterSet = scope !== 'mine' || includeDone || view !== 'all';

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Tasks</span></h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Your queue across all accounts.</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] text-gray-900 bg-[#B0FC38]/80 hover:bg-[#B0FC38] transition-colors"
          data-testid="tasks-new-button"
        >
          <Plus className="w-3.5 h-3.5" /> New Task
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-1.5">
          <FilterChip active={scope === 'mine'} onClick={() => updateParam('scope', 'mine')}>Assigned to me</FilterChip>
          <FilterChip active={scope === 'all'} onClick={() => updateParam('scope', 'all')}>All assignees</FilterChip>
        </div>
        <div className="flex items-center gap-1.5">
          <FilterChip active={view === 'all'} onClick={() => updateParam('view', null)}>All</FilterChip>
          <FilterChip active={view === 'due_today'} onClick={() => updateParam('view', 'due_today')}>Due today</FilterChip>
          <FilterChip active={view === 'overdue'} onClick={() => updateParam('view', 'overdue')}>Overdue</FilterChip>
          <FilterChip active={view === 'upcoming'} onClick={() => updateParam('view', 'upcoming')}>Next 7 days</FilterChip>
        </div>
        <label className="inline-flex items-center gap-2 text-[11px] text-gray-400 cursor-pointer select-none ml-auto">
          <input
            type="checkbox"
            checked={includeDone}
            onChange={(e) => updateParam('include_done', e.target.checked)}
            className="accent-[#B0FC38]"
          />
          Include done
        </label>
      </div>

      <PaginatedTable
        paginator={paginator}
        anyFilterSet={anyFilterSet}
        emptyMessage={scope === 'mine' ? 'No tasks assigned to you.' : 'No tasks yet.'}
        emptyFilteredMessage="No tasks match your filters."
        renderTable={(rows) => (
          <TasksList tasks={rows} onChange={onChange} showAccount />
        )}
      />

      <TaskFormDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        onSaved={() => {
          setShowNew(false);
          paginator.refetch();
        }}
      />
    </div>
  );
}
